import { Button, Modal, Row } from '@amzn/meridian';
import { Feature } from 'features/flags';
import { useState } from 'react';
import type { FlavorPath } from '../../mcc.types';
import { useActiveFlavors } from '../../useFlavor';
import { CopyPasteContentModalBody } from './CopyPasteContentModalBody';

/**
 * Props for the CopyPasteContentModalWrapper component
 *
 * @typedef {Object} CopyPasteContentModalWrapperProps
 * @property {FlavorPath} pathToFlavor - Path to the flavor content in the form data
 */
type CopyPasteContentModalWrapperProps = {
  pathToFlavor: FlavorPath;
};

/**
 * A wrapper component that provides a button to open the copy-paste content modal
 * and manages the modal state. The modal allows users to copy-paste creative content
 * in JSON format.
 *
 * This component is feature-flagged with PASTE_FROM_FIGMA and disables the button
 * when multiple flavors are active.
 *
 * @param {CopyPasteContentModalWrapperProps} props - Component props
 * @returns {React.ReactElement} The rendered component
 */
export const CopyPasteContentModalWrapper = ({ pathToFlavor }: CopyPasteContentModalWrapperProps) => {
  const [modalOpen, setModalOpen] = useState(false);

  const activeFlavors = useActiveFlavors();

  return (
    <Feature feature="PASTE_FROM_FIGMA">
      <Row alignmentHorizontal="start">
        <Button disabled={activeFlavors.length > 1} onClick={() => setModalOpen((c) => !c)} type="secondary">
          Paste from Figma
        </Button>
      </Row>
      <Modal
        fullscreenBreakpoint={1800}
        onClose={() => setModalOpen(false)}
        open={modalOpen}
        scrollContainer="modal"
        title="Copy + Paste Creative Content"
      >
        <CopyPasteContentModalBody onCloseModal={() => setModalOpen(false)} pathToFlavor={pathToFlavor} />
      </Modal>
    </Feature>
  );
};
